import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Anatomical region colors (Eonik Website Zinc Palette)
const REGION_META = {
  visual_cortex:  { color: new THREE.Color(0x2563eb), label: 'Visual Hook',      baseColor: new THREE.Color(0xa1a1aa) }, // Blue
  prefrontal:     { color: new THREE.Color(0x0ea5e9), label: 'Comprehension',    baseColor: new THREE.Color(0xa1a1aa) }, // Sky
  auditory:       { color: new THREE.Color(0xf59e0b), label: 'Audio Resonance',  baseColor: new THREE.Color(0xa1a1aa) }, // Amber
  limbic:         { color: new THREE.Color(0xef4444), label: 'Emotional Response', baseColor: new THREE.Color(0xa1a1aa) }, // Red
  motor:          { color: new THREE.Color(0x10b981), label: 'Action Intent',          baseColor: new THREE.Color(0xa1a1aa) }, // Emerald
};

// Classify a normalised vertex (0-1 in each axis) to one of the 5 brain regions.
// Convention assumed: +Y = superior, +Z = anterior, ±X = lateral
function classifyVertex(nx, ny, nz) {
  // Posterior (occipital) → Visual Cortex
  if (nz < 0.32) return 'visual_cortex';
  // Superior anterior → Motor cortex
  if (ny > 0.72 && nz > 0.35 && nz < 0.72) return 'motor';
  // Anterior top → Prefrontal
  if (nz > 0.68 && ny > 0.38) return 'prefrontal';
  // Lateral → Auditory (temporal lobes)
  if (nx < 0.22 || nx > 0.78) return 'auditory';
  // Default → Limbic (deep/central)
  return 'limbic';
}

export default function BrainVisualization({
  regionScores = {},
  currentTime = 0,
  duration = 1,
  timeline = {},
  vertexTimeline = null,
  vertexTimelineLabels = null,  // 256-element string array matching vertexTimeline columns
  nVertices = 0,
  nTimesteps = 0,
  hoveredRegion = null,
  onRegionHover,
  paused = false,
}) {
  const containerRef = useRef(null);
  const [overlayStats, setOverlayStats] = useState({
    timeSec: 0,
    frameIdx: 0,
    frameTotal: 0,
    regionStrength: {},
  });
  const [activeCallout, setActiveCallout] = useState({
    regionKey: null,
    label: '',
    strength: 0,
    x: 80,
    y: 80,
    visible: false,
  });
  const mountedRef = useRef(true);
  const statsUpdateTickRef = useRef(0);

  // mutable refs so animation loop always reads latest values
  const regionScoresRef   = useRef({});
  const currentTimeRef    = useRef(0);
  const timelineRef       = useRef(null);
  const vertexTimelineRef       = useRef(null);
  const vertexTimelineLabelsRef = useRef(null);
  const durationRef             = useRef(1);
  const nVerticesRef      = useRef(0);
  const nTimestepsRef     = useRef(0);
  const hoveredRef        = useRef(null);

  useEffect(() => { regionScoresRef.current = regionScores; }, [regionScores]);
  useEffect(() => { currentTimeRef.current  = currentTime;  }, [currentTime]);
  useEffect(() => { durationRef.current     = duration;     }, [duration]);
  useEffect(() => { timelineRef.current     = timeline;     }, [timeline]);
  useEffect(() => { vertexTimelineRef.current       = vertexTimeline;       }, [vertexTimeline]);
  useEffect(() => { vertexTimelineLabelsRef.current = vertexTimelineLabels; }, [vertexTimelineLabels]);
  useEffect(() => { nVerticesRef.current      = nVertices;      }, [nVertices]);
  useEffect(() => { nTimestepsRef.current     = nTimesteps;     }, [nTimesteps]);
  useEffect(() => { hoveredRef.current        = hoveredRegion; }, [hoveredRegion]);

  const pausedRef      = useRef(false);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const initScene = useCallback((container) => {
    const W = container.clientWidth;
    const H = container.clientHeight || 480;

    // ── Renderer ──────────────────────────────────────────────────────────────
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0; // Slightly lower exposure for less blown out whites
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.domElement.style.borderRadius = '12px';
    container.appendChild(renderer.domElement);

    // ── Scene / Camera ────────────────────────────────────────────────────────
    const scene  = new THREE.Scene();
    scene.background = null; // Let the React container's background show through
    // Removed fog: Light mode fog washes out depth contrast
    
    const camera = new THREE.PerspectiveCamera(42, W / H, 0.1, 100);
    camera.position.set(0, 0.4, 5.5);

    // ── Orbit Controls ────────────────────────────────────────────────────────
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping   = true;
    controls.dampingFactor   = 0.06;
    controls.autoRotate      = true;
    controls.autoRotateSpeed = 0.6;
    controls.enableZoom      = true;
    controls.minDistance     = 3;
    controls.maxDistance     = 9;
    controls.enablePan       = false;

    // ── Lighting (Calibrated for Light Theme) ──────────────────────────────────
    scene.add(new THREE.AmbientLight(0xffffff, 0.45)); // Soft ambient for white background

    const rim1 = new THREE.DirectionalLight(0x2563eb, 0.6); // Eonik blue soft rim
    rim1.position.set(-4, 3, 2);
    scene.add(rim1);

    const rim2 = new THREE.DirectionalLight(0xa1a1aa, 0.4); 
    rim2.position.set(4, -2, -3);
    scene.add(rim2);

    const topLight = new THREE.DirectionalLight(0xffffff, 0.5); 
    topLight.position.set(0, 8, 0);
    scene.add(topLight);

    // ── Effect Composer (Bloom) ────────────────────────────────────────────────
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));

    const bloom = new UnrealBloomPass(
      new THREE.Vector2(W, H),
      1.4,   // strength
      0.5,   // radius
      0.05,  // threshold (low = almost everything glows a little)
    );
    composer.addPass(bloom);
    composer.addPass(new OutputPass());

    // ── State shared with loader & animation ───────────────────────────────────
    const brainGroup = new THREE.Group();
    scene.add(brainGroup);

    // Per-vertex region index (set after mesh loaded)
    const meshData = [];           // [{ mesh, regionIndices: Float32Array }]
    const regionMarkers = {};      // key → { mesh, glow }
    const connectionLines = [];

    // ── Brain GLB Loader ───────────────────────────────────────────────────────
    const loader = new GLTFLoader();
    loader.load(
      '/models/brain.glb',
      (gltf) => {
        const root = gltf.scene;
        // Auto-centre & scale
        const box  = new THREE.Box3().setFromObject(root);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale  = 2.8 / maxDim;
        root.scale.setScalar(scale);
        const centre = box.getCenter(new THREE.Vector3()).multiplyScalar(-scale);
        root.position.copy(centre);
        brainGroup.add(root);

        // Re-compute bbox in world space after scaling
        const worldBox = new THREE.Box3().setFromObject(root);
        const wSize    = worldBox.getSize(new THREE.Vector3());
        const wMin     = worldBox.min;

        root.traverse((child) => {
          if (!child.isMesh) return;

          const pos = child.geometry.attributes.position;
          const n   = pos.count;

          // Build per-vertex region index (encoded as float for vertex color)
          const regionIndex = new Float32Array(n); // 0..4
          const colors      = new Float32Array(n * 3);

          for (let i = 0; i < n; i++) {
            const lx = pos.getX(i) * child.scale.x + child.position.x;
            const ly = pos.getY(i) * child.scale.y + child.position.y;
            const lz = pos.getZ(i) * child.scale.z + child.position.z;

            // world coords via matrix
            const vWorld = new THREE.Vector3(lx, ly, lz).applyMatrix4(child.matrixWorld);
            const nx = (vWorld.x - wMin.x) / wSize.x;
            const ny = (vWorld.y - wMin.y) / wSize.y;
            const nz = (vWorld.z - wMin.z) / wSize.z;

            const region = classifyVertex(
              Math.max(0, Math.min(1, nx)),
              Math.max(0, Math.min(1, ny)),
              Math.max(0, Math.min(1, nz)),
            );
            const rIdx = Object.keys(REGION_META).indexOf(region);
            regionIndex[i] = rIdx;

            // Start with light base color
            const base = REGION_META[region].baseColor;
            colors[i * 3]     = base.r;
            colors[i * 3 + 1] = base.g;
            colors[i * 3 + 2] = base.b;
          }

          child.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

          child.material = new THREE.MeshStandardMaterial({
            vertexColors: true,
            roughness:    0.35, // Sharper, more glossy finish than 0.55
            metalness:    0.15, // Slight metallic edge to catch the rim lights
            transparent:  true,
            opacity:      0.96, // Highly solid so it doesn't wash out
            emissive:     new THREE.Color(0x000000),
          });

          meshData.push({ mesh: child, regionIndex });
        });

        // Region marker spheres (inside the brain mesh)
        const regionPositions = computeRegionCentroids(root, worldBox);
        buildRegionMarkers(scene, regionPositions, regionMarkers);
        buildConnectionLines(scene, regionPositions, connectionLines);
      },
      undefined,
      (err) => {
        console.warn('Brain GLB failed, using fallback sphere', err);
        buildFallbackBrain(brainGroup, meshData, regionMarkers, scene, connectionLines);
      },
    );

    // ── Neural Spark Particles (REMOVED FOR CLEANER UX) ───────────────────────
    // User requested removal of confusing floating dots.

    // ── Raycaster for hover ────────────────────────────────────────────────────
    const mouse      = new THREE.Vector2();
    const raycaster  = new THREE.Raycaster();
    const onMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouse.x =  ((e.clientX - rect.left)  / rect.width)  * 2 - 1;
      mouse.y = -((e.clientY - rect.top)   / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(
        Object.values(regionMarkers).map(m => m.mesh),
      );
      if (hits.length > 0) {
        const key = hits[0].object.userData.regionKey;
        onRegionHover?.(key);
        container.style.cursor = 'pointer';
      } else {
        onRegionHover?.(null);
        container.style.cursor = 'default';
      }
    };
    container.addEventListener('mousemove', onMouseMove);

    // ── Animation Loop ─────────────────────────────────────────────────────────
    let rafId;
    let frame = 0;

    const animate = () => {
      rafId = requestAnimationFrame(animate);
      frame++;
      const t      = Date.now() * 0.001;
      const scores = regionScoresRef.current   || {};
      const tl     = timelineRef.current       || {};
      const vtl    = vertexTimelineRef.current        || null;
      const vtlLbl = vertexTimelineLabelsRef.current  || null;
      const dur    = durationRef.current              || 1;
      const nV     = nVerticesRef.current      || 256;
      const nT     = nTimestepsRef.current     || 0;
      const ct     = currentTimeRef.current    || 0;
      const hov    = hoveredRef.current;

      // Current attention from timeline
      let attention = 50;
      if (tl.attention?.length) {
        const idx = Math.min(Math.floor(ct), tl.attention.length - 1);
        attention = tl.attention[idx] || 50;
      }
      const attNorm = attention / 100;

      // Determine current frame index in the vertex timeline.
      // Driven by the shared currentTime (video clock) so the brain, video,
      // and engagement curves are all locked to the same playback position.
      let vFrame = null;
      let dynamicRegionScores = null;
      if (vtl && vtl.length > 0 && dur > 0) {
        // Use currentTime (video seconds) to derive frame index from the
        // actual timeline length so visual updates stay in sync.
        const timelineLen = vtl.length;
        const frameIdx = Math.min(
          timelineLen - 1,
          Math.max(0, Math.floor((ct / Math.max(dur, 1)) * timelineLen)),
        );
        vFrame = vtl[frameIdx];
        
        // Aggregate vertex activations into regions using the real anatomical labels
        if (vFrame && vFrame.length > 0) {
          dynamicRegionScores = {};
          const rKeys = Object.keys(REGION_META);
          const sums = {}, cnts = {};
          rKeys.forEach(k => { sums[k] = 0; cnts[k] = 0; });

          for (let j = 0; j < vFrame.length; j++) {
            // Use server-provided anatomical label if available, else fall back to equal chunks
            const label = vtlLbl?.[j] || rKeys[Math.floor(j / Math.ceil(vFrame.length / 5))];
            if (sums[label] !== undefined) {
              sums[label] += Math.abs(vFrame[j] || 0);
              cnts[label]++;
            }
          }
          const globalMax = Math.max(...rKeys.map(k => (cnts[k] > 0 ? sums[k] / cnts[k] : 0)), 0.001);
          rKeys.forEach(k => {
            const avg = cnts[k] > 0 ? sums[k] / cnts[k] : 0;
            dynamicRegionScores[k] = Math.min(1, avg / globalMax);
          });

          // Update UI overlay at a throttled cadence.
          statsUpdateTickRef.current += 1;
          if (statsUpdateTickRef.current % 6 === 0 && mountedRef.current) {
            const regionStrength = {};
            rKeys.forEach((k) => {
              regionStrength[k] = Math.round((dynamicRegionScores[k] || 0) * 100);
            });
            setOverlayStats({
              timeSec: Math.round(ct),
              frameIdx: frameIdx + 1,
              frameTotal: timelineLen,
              regionStrength,
            });
          }
        }
      }

      if (!dynamicRegionScores) {
        const fallbackStrength = {};
        Object.keys(REGION_META).forEach((k) => {
          fallbackStrength[k] = Math.round(((scores[k] ?? 50) / 100) * 100);
        });
        statsUpdateTickRef.current += 1;
        if (statsUpdateTickRef.current % 10 === 0 && mountedRef.current) {
          setOverlayStats({
            timeSec: Math.round(ct),
            frameIdx: 0,
            frameTotal: nT || 0,
            regionStrength: fallbackStrength,
          });
        }
      }

      // Drive a practical, obvious callout pointing at the most active region.
      // This is what makes the animation interpretable for non-technical users.
      if (mountedRef.current && statsUpdateTickRef.current % 6 === 0) {
        const regionStrengths = {};
        Object.keys(REGION_META).forEach((k) => {
          regionStrengths[k] = dynamicRegionScores
            ? Math.round((dynamicRegionScores[k] || 0) * 100)
            : Math.round(((scores[k] ?? 50) / 100) * 100);
        });
        let topKey = null;
        let topVal = -1;
        Object.entries(regionStrengths).forEach(([k, v]) => {
          if (v > topVal) {
            topVal = v;
            topKey = k;
          }
        });

        if (topKey && regionMarkers[topKey]?.mesh) {
          const p = regionMarkers[topKey].mesh.position.clone().project(camera);
          const w = container.clientWidth || 1;
          const h = container.clientHeight || 1;
          const screenX = (p.x * 0.5 + 0.5) * w;
          const screenY = (-p.y * 0.5 + 0.5) * h;
          const clampedX = Math.max(80, Math.min(w - 80, screenX));
          const clampedY = Math.max(44, Math.min(h - 44, screenY));
          const visible = p.z > -1 && p.z < 1.1;

          setActiveCallout({
            regionKey: topKey,
            label: REGION_META[topKey].label,
            strength: topVal,
            x: clampedX,
            y: clampedY,
            visible,
          });
        }
      }

      // Update vertex colors for each brain mesh chunk
      for (const { mesh, regionIndex } of meshData) {
        const colAttr = mesh.geometry.attributes.color;
        const count   = colAttr.count;
        const regionKeys = Object.keys(REGION_META);

        for (let i = 0; i < count; i++) {
          const rIdx   = Math.round(regionIndex[i]);
          const rKey   = regionKeys[rIdx] || 'limbic';
          
          let bright = 0.12;
          const score  = (scores[rKey] ?? 50) / 100;
          const hbonus = hov === rKey ? 0.35 : 0;

          if (dynamicRegionScores) {
            // Live mode: dynamic intensity must clearly track video progress.
            const dynamicIntensity = dynamicRegionScores[rKey] || 0;
            const breathing = 0.03 + Math.sin(t * 1.4 + rIdx * 0.9) * 0.03;
            bright = Math.max(0, Math.min(1, 0.08 + (dynamicIntensity * 0.92) + breathing + hbonus));
          } else {
            // Fallback mode when vertex timeline is unavailable.
            const pulse  = 0.55 + Math.sin(t * 2.4 + rIdx * 1.3) * 0.18;
            bright = Math.max(0, Math.min(1, 0.10 + (score * pulse * 0.72) + hbonus));
          }

          const full = REGION_META[rKey].color;
          const base = REGION_META[rKey].baseColor;

          colAttr.array[i * 3]     = base.r + (full.r - base.r) * bright;
          colAttr.array[i * 3 + 1] = base.g + (full.g - base.g) * bright;
          colAttr.array[i * 3 + 2] = base.b + (full.b - base.b) * bright;
        }
        colAttr.needsUpdate = true;
      }

      // Update region marker spheres
      for (const [key, { mesh: m, glow: g }] of Object.entries(regionMarkers)) {
        const score = dynamicRegionScores
          ? (dynamicRegionScores[key] || 0)
          : (scores[key] ?? 50) / 100;
        const pulse  = 0.78 + Math.sin(t * 2.4 + Object.keys(REGION_META).indexOf(key)) * 0.22;
        const hbonus = hov === key ? 0.6 : 0;
        const intens = (score * pulse) + hbonus;

        m.material.emissiveIntensity = Math.min(2.8, intens * 2.2 + 0.2);
        if (g?.material) {
          g.material.opacity = Math.min(0.85, intens * 0.65 + hbonus * 0.2);
          g.scale.setScalar(0.96 + (score * 0.35) + Math.sin(t * 1.8 + key.length) * 0.06);
        }
        m.scale.setScalar(0.88 + (score * 0.28) + Math.sin(t * 2.1 + key.length * 0.7) * 0.05);
      }

      // Update connection lines
      for (const line of connectionLines) {
        const { obj, keyA, keyB } = line;
        const sA = dynamicRegionScores
          ? (dynamicRegionScores[keyA] || 0)
          : (scores[keyA] ?? 50) / 100;
        const sB = dynamicRegionScores
          ? (dynamicRegionScores[keyB] || 0)
          : (scores[keyB] ?? 50) / 100;
        const strength = (sA + sB) / 2;
        obj.material.opacity = Math.max(0.03, Math.min(0.68, strength * 0.92)) * attNorm;
      }

      // Sparks updated logic removed for cleaner UI

      controls.update();
      composer.render();
    };

    animate();

    // ── Resize ─────────────────────────────────────────────────────────────────
    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight || 480;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      container.removeEventListener('mousemove', onMouseMove);
      cancelAnimationFrame(rafId);
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      composer.dispose();
    };
  }, [onRegionHover]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    return initScene(container);
  }, [initScene]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', minHeight: '460px', borderRadius: '12px', overflow: 'hidden', position: 'relative' }}
      data-testid="brain-visualization-canvas"
    >
      {/* Live activation panel */}
      <div style={{
        position: 'absolute', top: 10, left: 12, zIndex: 11,
        minWidth: 190,
        padding: '8px 10px',
        borderRadius: 8,
        background: 'rgba(255,255,255,0.88)',
        border: '1px solid rgba(212,212,216,0.9)',
        backdropFilter: 'blur(6px)',
        pointerEvents: 'none',
      }}>
        <p style={{
          margin: 0, marginBottom: 6,
          fontSize: 9, letterSpacing: '0.09em',
          fontFamily: 'IBM Plex Mono, monospace',
          color: '#52525B',
        }}>
          LIVE ACTIVATION · t={overlayStats.timeSec}s
          {overlayStats.frameTotal > 0 ? ` · frame ${overlayStats.frameIdx}/${overlayStats.frameTotal}` : ''}
        </p>
        {Object.entries(REGION_META).map(([key, meta]) => {
          const strength = overlayStats.regionStrength[key] ?? Math.round(((regionScores[key] ?? 50)));
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: `#${meta.color.getHexString()}`, flexShrink: 0 }} />
              <span style={{
                width: 70, fontSize: 9, color: '#71717A',
                fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.03em',
              }}>
                {meta.label}
              </span>
              <div style={{
                flex: 1, height: 5, borderRadius: 999,
                background: '#E4E4E7', overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.max(3, Math.min(100, strength))}%`,
                  background: `#${meta.color.getHexString()}`,
                  borderRadius: 999,
                  transition: 'width 0.18s ease-out',
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Region legend overlay */}
      <div style={{
        position: 'absolute', bottom: 12, left: 12, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 4,
        pointerEvents: 'none',
      }}>
        {Object.entries(REGION_META).map(([key, { color, label }]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: `#${color.getHexString()}`,
              boxShadow: `0 0 6px #${color.getHexString()}`,
            }} />
            <span style={{
              fontSize: 10, color: '#71717A',
              fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.04em',
            }}>
              {label.toUpperCase()}
            </span>
          </div>
        ))}
      </div>

      {/* Dynamic label with pointer to active region */}
      {activeCallout.visible && activeCallout.regionKey && (
        <div
          style={{
            position: 'absolute',
            left: activeCallout.x,
            top: activeCallout.y,
            transform: 'translate(-50%, calc(-100% - 14px))',
            zIndex: 13,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              padding: '7px 10px',
              borderRadius: 8,
              background: 'rgba(9,9,11,0.86)',
              border: `1px solid rgba(255,255,255,0.16)`,
              boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
              minWidth: 150,
            }}
          >
            <p style={{
              margin: 0,
              fontSize: 9,
              color: '#A1A1AA',
              fontFamily: 'IBM Plex Mono, monospace',
              letterSpacing: '0.06em',
            }}>
              SECTION ACTIVE NOW
            </p>
            <p style={{
              margin: '2px 0 0',
              fontSize: 11,
              color: '#FFFFFF',
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
              lineHeight: 1.3,
            }}>
              {activeCallout.label} · {activeCallout.strength}%
            </p>
          </div>
          <div
            style={{
              width: 10,
              height: 10,
              margin: '-1px auto 0',
              background: 'rgba(9,9,11,0.86)',
              borderRight: '1px solid rgba(255,255,255,0.16)',
              borderBottom: '1px solid rgba(255,255,255,0.16)',
              transform: 'rotate(45deg)',
            }}
          />
        </div>
      )}

      {/* Drag hint */}
      <div style={{
        position: 'absolute', top: 10, right: 12, zIndex: 10,
        fontSize: 9, color: '#A1A1AA',
        fontFamily: 'IBM Plex Mono, monospace', pointerEvents: 'none',
        letterSpacing: '0.06em',
      }}>
        DRAG TO ROTATE
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function computeRegionCentroids(root, worldBox) {
  const regionKeys = Object.keys(REGION_META);
  const sums   = regionKeys.map(() => new THREE.Vector3());
  const counts = regionKeys.map(() => 0);
  const wMin   = worldBox.min;
  const wSize  = worldBox.getSize(new THREE.Vector3());

  root.traverse((child) => {
    if (!child.isMesh) return;
    const pos = child.geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const v = new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(child.matrixWorld);
      const nx = (v.x - wMin.x) / wSize.x;
      const ny = (v.y - wMin.y) / wSize.y;
      const nz = (v.z - wMin.z) / wSize.z;
      const rKey = classifyVertex(
        Math.max(0, Math.min(1, nx)),
        Math.max(0, Math.min(1, ny)),
        Math.max(0, Math.min(1, nz)),
      );
      const idx = regionKeys.indexOf(rKey);
      if (idx >= 0) {
        sums[idx].add(v);
        counts[idx]++;
      }
    }
  });

  const centroids = {};
  regionKeys.forEach((k, i) => {
    centroids[k] = counts[i] > 0
      ? sums[i].divideScalar(counts[i])
      : new THREE.Vector3(0, 0, 0);
  });
  return centroids;
}

function buildRegionMarkers(scene, positions, out) {
  for (const [key, meta] of Object.entries(REGION_META)) {
    const pos = positions[key] || new THREE.Vector3();

    // Core sphere
    const geo  = new THREE.SphereGeometry(0.08, 16, 16);
    const mat  = new THREE.MeshStandardMaterial({
      color:            meta.color,
      emissive:         meta.color,
      emissiveIntensity: 1.2,
      transparent:      true,
      opacity:          0.95,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    mesh.userData.regionKey = key;
    scene.add(mesh);

    // Halo glow
    const glowGeo = new THREE.SphereGeometry(0.16, 12, 12);
    const glowMat = new THREE.MeshBasicMaterial({
      color: meta.color, transparent: true, opacity: 0.25, depthWrite: false,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    glow.position.copy(pos);
    scene.add(glow);

    out[key] = { mesh, glow };
  }
}

function buildConnectionLines(scene, positions, out) {
  const keys  = Object.keys(REGION_META);
  const pairs = [
    ['prefrontal', 'motor'],
    ['motor',      'visual_cortex'],
    ['prefrontal', 'limbic'],
    ['limbic',     'auditory'],
    ['auditory',   'visual_cortex'],
  ];

  for (const [kA, kB] of pairs) {
    const pA = positions[kA] || new THREE.Vector3();
    const pB = positions[kB] || new THREE.Vector3();

    const points = [];
    const segments = 20;
    for (let i = 0; i <= segments; i++) {
      const r   = i / segments;
      const mid = pA.clone().lerp(pB, r);
      // arc outward
      mid.multiplyScalar(1 + Math.sin(r * Math.PI) * 0.22);
      points.push(mid);
    }

    const geo = new THREE.BufferGeometry().setFromPoints(points);
    const col = REGION_META[kA].color.clone().lerp(REGION_META[kB].color, 0.5);
    const mat = new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.18, depthWrite: false });
    const obj = new THREE.Line(geo, mat);
    scene.add(obj);
    out.push({ obj, keyA: kA, keyB: kB });
  }
}

function buildFallbackBrain(group, meshData, regionMarkers, scene, connectionLines) {
  // Simple lobe shapes as fallback
  const lobes = [
    { key: 'visual_cortex', pos: [0, -0.3, -1.1], rx: 0.5, ry: 0.6 },
    { key: 'prefrontal',    pos: [0,  0.3,  1.0], rx: 0.55, ry: 0.55 },
    { key: 'auditory',      pos: [1.0, 0,   0.1], rx: 0.4, ry: 0.5 },
    { key: 'limbic',        pos: [0,  -0.1,  0.1], rx: 0.45, ry: 0.4 },
    { key: 'motor',         pos: [0,   0.9, -0.1], rx: 0.55, ry: 0.4 },
  ];

  for (const { key, pos, rx, ry } of lobes) {
    const meta = REGION_META[key];
    const geo  = new THREE.SphereGeometry(1, 32, 32);
    geo.scale(rx, ry, rx);
    const mat  = new THREE.MeshStandardMaterial({
      color: meta.baseColor, emissive: meta.color, emissiveIntensity: 0.6,
      transparent: true, opacity: 0.72, roughness: 0.5,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(...pos);
    mesh.userData.regionKey = key;
    group.add(mesh);

    // For fallback, also put into regionMarkers for hover
    const glowMat = new THREE.MeshBasicMaterial({ color: meta.color, transparent: true, opacity: 0.18, depthWrite: false });
    const glow    = new THREE.Mesh(new THREE.SphereGeometry(1.1, 16, 16), glowMat);
    glow.position.set(...pos);
    group.add(glow);
    regionMarkers[key] = { mesh, glow };
  }
}
